/**
 * Funding / payer taxonomy helpers — data rows live in
 * `shared/taxonomy/funding_taxonomy.json` (generated → `../generated/fundingTaxonomy`).
 */

import { extractZipFromLocation } from "../utils/patientCareJourney";
import { extractUsStateCodeFromLocationTail } from "./usStates";
import {
  extractUsZip3,
  getZip3PrimaryState,
} from "../utils/providerSearchLocationDisplay";
import {
  FUNDING_DEFAULT_STATES as GENERATED_FUNDING_DEFAULT_STATES,
  FUNDING_TAXONOMY as GENERATED_FUNDING_TAXONOMY,
  FUNDING_TAXONOMY_VERSION,
} from "../generated/fundingTaxonomy";

export { FUNDING_TAXONOMY_VERSION };

export type AcceptsFlag =
  | "medicaid"
  | "medicare"
  | "private_insurance"
  | "private_pay"
  | "state_waivers";

export type CasePayerCode =
  | "medicaid"
  | "medicare"
  | "private_pay"
  | "other"
  | "unknown";

export interface FundingOption {
  code: string;
  label: string;
  providerLabel?: string;
  /** Omit or `["*"]` = national; USPS codes e.g. `["MN"]` for state programs. */
  states?: readonly string[];
  acceptsFlags?: readonly AcceptsFlag[];
  payerClass?: string;
  providerSelectable: boolean;
  casePrimaryEligible: boolean;
  searchPayerClass: boolean;
  searchProgram: boolean;
  searchFilterReady: boolean;
  aliases?: readonly string[];
}

/** Beachhead default until multi-state packs ship. */
export const FUNDING_DEFAULT_STATES = GENERATED_FUNDING_DEFAULT_STATES;

export const FUNDING_TAXONOMY: readonly FundingOption[] =
  GENERATED_FUNDING_TAXONOMY;

export interface CasePayerOption {
  value: CasePayerCode;
  label: string;
}

export interface FundingSelectOption {
  value: string;
  label: string;
}

export type FundingContextSource =
  | "explicit"
  | "state"
  | "query"
  | "zip"
  | "default";

export interface FundingTaxonomyContext {
  states?: string[];
  /** How states were resolved — used for accurate scope copy. */
  source?: FundingContextSource;
}

const CASE_PAYER_CODES: CasePayerCode[] = [
  "unknown",
  "medicaid",
  "medicare",
  "private_pay",
  "other",
];

/** Canonical case primary-payer options (national labels). Single Unknown. */
export const CASE_PAYER_OPTIONS: readonly CasePayerOption[] =
  CASE_PAYER_CODES.map((code) => {
    const row = FUNDING_TAXONOMY.find((option) => option.code === code);
    return {
      value: code,
      label: row?.label ?? code,
    };
  });

const CASE_PAYER_VALUES = new Set<string>(CASE_PAYER_CODES);

const CODE_BY_ALIAS = (() => {
  const map = new Map<string, string>();
  for (const option of FUNDING_TAXONOMY) {
    map.set(option.code.toLowerCase(), option.code);
    map.set(option.label.toLowerCase(), option.code);
    if (option.providerLabel) {
      map.set(option.providerLabel.toLowerCase(), option.code);
    }
    for (const alias of option.aliases ?? []) {
      map.set(alias.toLowerCase(), option.code);
    }
  }
  return map;
})();

function resolveContextStates(ctx?: FundingTaxonomyContext): string[] {
  const states = ctx?.states
    ?.map((state) => state.trim().toUpperCase())
    .filter(Boolean);
  if (states && states.length > 0) {
    return states;
  }
  return [...FUNDING_DEFAULT_STATES];
}

/** Pull a US state code from free-text location ("City, MN 55401" or "Austin, Texas"). */
export function extractStateCodeFromLocationQuery(
  locationQuery?: string | null
): string | null {
  if (!locationQuery?.trim()) {
    return null;
  }
  return extractUsStateCodeFromLocationTail(locationQuery);
}

export type FundingContextHints = {
  /** Explicit multi-state context (wins when non-empty). */
  states?: string[] | null;
  /** Provider / filter state code. */
  state?: string | null;
  zipCode?: string | null;
  locationQuery?: string | null;
};

/**
 * Resolve taxonomy program context states (plan order):
 * explicit states → state → location query state → ZIP → beachhead default (MN).
 */
export function resolveFundingContextDetailed(hints?: FundingContextHints): {
  states: string[];
  source: FundingContextSource;
} {
  const explicitStates = hints?.states
    ?.map((state) => state.trim().toUpperCase())
    .filter(Boolean);
  if (explicitStates && explicitStates.length > 0) {
    return { states: explicitStates, source: "explicit" };
  }

  const state = hints?.state?.trim().toUpperCase();
  if (state && /^[A-Z]{2}$/.test(state)) {
    return { states: [state], source: "state" };
  }

  const fromQuery = extractStateCodeFromLocationQuery(hints?.locationQuery);
  if (fromQuery) {
    return { states: [fromQuery], source: "query" };
  }

  const zip =
    hints?.zipCode?.trim() ||
    extractZipFromLocation(hints?.locationQuery) ||
    null;
  const fromZip = getZip3PrimaryState(extractUsZip3(zip));
  if (fromZip) {
    return { states: [fromZip], source: "zip" };
  }

  return { states: [...FUNDING_DEFAULT_STATES], source: "default" };
}

export function resolveFundingContextStates(
  hints?: FundingContextHints
): string[] {
  return resolveFundingContextDetailed(hints).states;
}

export function fundingTaxonomyContextFromHints(
  hints?: FundingContextHints
): FundingTaxonomyContext {
  const resolved = resolveFundingContextDetailed(hints);
  return { states: resolved.states, source: resolved.source };
}

function rowMatchesStates(
  option: FundingOption,
  contextStates: string[]
): boolean {
  if (!option.states || option.states.length === 0) {
    return true;
  }
  if (option.states.includes("*")) {
    return true;
  }
  return option.states.some((state) =>
    contextStates.includes(state.toUpperCase())
  );
}

function toSelectOption(
  option: FundingOption,
  audience: "case" | "provider" | "search" = "search"
): FundingSelectOption {
  const label =
    audience === "provider" && option.providerLabel
      ? option.providerLabel
      : option.label;
  return { value: option.code, label };
}

export function getCasePayerOptions(): CasePayerOption[] {
  return [...CASE_PAYER_OPTIONS];
}

export function getProviderFundingOptions(
  ctx?: FundingTaxonomyContext
): FundingSelectOption[] {
  const states = resolveContextStates(ctx);
  return FUNDING_TAXONOMY.filter(
    (option) => option.providerSelectable && rowMatchesStates(option, states)
  ).map((option) => toSelectOption(option, "provider"));
}

export function getSearchPayerClassOptions(): FundingSelectOption[] {
  return FUNDING_TAXONOMY.filter(
    (option) => option.searchPayerClass && option.searchFilterReady
  ).map((option) => toSelectOption(option, "search"));
}

export function getSearchProgramOptions(
  ctx?: FundingTaxonomyContext
): FundingSelectOption[] {
  const states = resolveContextStates(ctx);
  return FUNDING_TAXONOMY.filter(
    (option) =>
      option.searchProgram &&
      option.searchFilterReady &&
      rowMatchesStates(option, states)
  ).map((option) => toSelectOption(option, "search"));
}

/**
 * Additional case funding programs. Primary payer classes are excluded so
 * Medicaid, Medicare, and Private pay cannot be represented twice.
 */
export function getCaseMustHaveFundingOptions(
  ctx?: FundingTaxonomyContext
): FundingSelectOption[] {
  return getSearchProgramOptions(ctx).filter(
    (option) => option.value !== "any_state_waiver"
  );
}

/**
 * Keep already-selected codes visible when state context changes
 * (e.g. case ZIP flips WI↔MN, or case-prefilled CADI while location is empty).
 */
export function withSelectedFundingOptions(
  options: FundingSelectOption[],
  selected: string[] | null | undefined,
  audience: "case" | "provider" | "search" = "search"
): FundingSelectOption[] {
  if (!selected?.length) {
    return options;
  }
  const byValue = new Map(options.map((option) => [option.value, option]));
  for (const raw of selected) {
    const code = resolveFundingCode(raw) ?? raw.trim();
    if (!code || byValue.has(code)) {
      continue;
    }
    const row = FUNDING_TAXONOMY.find((option) => option.code === code);
    if (!row) {
      continue;
    }
    byValue.set(code, toSelectOption(row, audience));
  }
  // Preserve taxonomy order, then any extras.
  const ordered: FundingSelectOption[] = [];
  const seen = new Set<string>();
  for (const row of FUNDING_TAXONOMY) {
    const option = byValue.get(row.code);
    if (option) {
      ordered.push(option);
      seen.add(row.code);
    }
  }
  for (const option of byValue.values()) {
    if (!seen.has(option.value)) {
      ordered.push(option);
    }
  }
  return ordered;
}

/** Short helper under program lists when state packs are in play. */
export function formatFundingProgramScopeNote(
  ctx?: FundingTaxonomyContext
): string {
  const states = resolveContextStates(ctx);
  const source = ctx?.source ?? (ctx?.states?.length ? "explicit" : "default");

  if (source === "default") {
    return "Showing Minnesota programs by default. Set a location to match other states.";
  }

  if (states.length === 1 && states[0] === "MN") {
    return "Includes Minnesota waiver programs for this location.";
  }
  if (states.length === 1) {
    return `State programs shown for ${states[0]} (national options always included).`;
  }
  return `State programs shown for ${states.join(
    ", "
  )} (national options always included).`;
}

/**
 * Build taxonomy context from case placement location fields.
 */
export function fundingTaxonomyContextFromCaseLocation(input: {
  locationZip?: string | null;
  locationCity?: string | null;
  locationState?: string | null;
}): FundingTaxonomyContext {
  const city = input.locationCity?.trim() || "";
  const zip = input.locationZip?.trim() || "";
  const locationQuery = [city, zip].filter(Boolean).join(" ") || null;
  return fundingTaxonomyContextFromHints({
    state: input.locationState,
    zipCode: zip || null,
    locationQuery,
  });
}

export function getFundingLabel(
  code: string,
  audience: "case" | "provider" | "search" = "case"
): string {
  // Legacy "private" (commercial insurance import) is shown as "Private pay" on
  // the provider detail tab — it maps to the same payer class for providers.
  // The "private_pay" code is the canonical selectable option going forward.
  const resolvedCode =
    audience === "provider" && code === "private" ? "private_pay" : code;
  const option = FUNDING_TAXONOMY.find((row) => row.code === resolvedCode);
  if (!option) {
    return code;
  }
  if (audience === "provider" && option.providerLabel) {
    return option.providerLabel;
  }
  return option.label;
}

/**
 * Display labels for stored funding_accepted (codes or legacy labels).
 * Unmapped tokens are kept as trimmed free text so partial migrations still render.
 */
export function formatFundingAcceptedForDisplay(
  raw: string | string[] | null | undefined,
  audience: "case" | "provider" | "search" = "provider"
): string[] {
  let items: string[] = [];
  if (Array.isArray(raw)) {
    items = raw.map((item) => String(item));
  } else if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        items = parsed.map((item) => String(item));
      } else {
        items = raw.split(",").map((part) => part.trim());
      }
    } catch {
      items = raw.split(",").map((part) => part.trim());
    }
  }

  const labels: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const trimmed = item.trim();
    if (!trimmed) {
      continue;
    }
    const code = resolveFundingCode(trimmed);
    const label = code ? getFundingLabel(code, audience) : trimmed;
    if (seen.has(label)) {
      continue;
    }
    seen.add(label);
    labels.push(label);
  }
  return labels;
}

export type ProviderPaymentAcceptedSource = {
  funding_accepted?: string | string[] | null;
  accepts_medicaid?: boolean | null;
  accepts_medicare?: boolean | null;
  accepts_private_insurance?: boolean | null;
};

/**
 * Directory/payment summary labels for map popup, profile, cards.
 *
 * For `"provider"` audience, the legacy `"private"` code (commercial insurance
 * import) is shown as `"Private pay"` — same label as `"private_pay"` — because
 * `"private"` is no longer selectable by providers; `"private_pay"` is canonical.
 * `getFundingLabel("private", "provider")` returns `"Private pay"` via code
 * remapping in that function.
 *
 * For `"search"` audience, legacy `accepts_private_insurance` / funding code
 * `"private"` are also collapsed to `"Private pay"` for display consistency.
 */
export function formatProviderPaymentAcceptedDisplay(
  provider: ProviderPaymentAcceptedSource,
  audience: "case" | "provider" | "search" = "search"
): string[] {
  if (audience === "search") {
    return formatSearchDirectoryPaymentAccepted(provider);
  }

  const labels: string[] = [];
  const seen = new Set<string>();
  const push = (label: string) => {
    const key = label.trim().toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    labels.push(label);
  };

  for (const label of formatFundingAcceptedForDisplay(
    provider.funding_accepted,
    audience
  )) {
    push(label);
  }

  const storedCodes = normalizeFundingAcceptedStored(provider.funding_accepted);
  const implied = fundingCodesToAcceptsFlags(storedCodes);

  if (provider.accepts_medicaid && !implied.acceptsMedicaid) {
    push(getFundingLabel("medicaid", audience));
  }
  if (provider.accepts_medicare && !implied.acceptsMedicare) {
    push(getFundingLabel("medicare", audience));
  }
  if (provider.accepts_private_insurance && !implied.acceptsPrivateInsurance) {
    push(getFundingLabel("private", audience));
  }

  return labels;
}

/** Map/list Accepts summary aligned with Payment accepted filter labels. */
function formatSearchDirectoryPaymentAccepted(
  provider: ProviderPaymentAcceptedSource
): string[] {
  const codes = new Set(
    normalizeFundingAcceptedStored(provider.funding_accepted)
  );
  const implied = fundingCodesToAcceptsFlags([...codes]);
  const labels: string[] = [];

  if (
    provider.accepts_medicaid ||
    implied.acceptsMedicaid ||
    codes.has("medicaid")
  ) {
    labels.push(getFundingLabel("medicaid", "search"));
  }
  if (
    provider.accepts_medicare ||
    implied.acceptsMedicare ||
    codes.has("medicare")
  ) {
    labels.push(getFundingLabel("medicare", "search"));
  }

  const hasPrivatePay =
    codes.has("private_pay") ||
    codes.has("private") ||
    provider.accepts_private_insurance === true;
  if (hasPrivatePay) {
    labels.push(getFundingLabel("private_pay", "search"));
  }

  // Surface other funding programs (waivers, LTCI, VA, …) without re-listing
  // private / private_pay (already shown as Private pay above).
  const seen = new Set(labels.map((l) => l.toLowerCase()));
  for (const label of formatFundingAcceptedForDisplay(
    provider.funding_accepted,
    "search"
  )) {
    const key = label.trim().toLowerCase();
    if (
      !key ||
      seen.has(key) ||
      key === "private insurance" ||
      key === "private pay" ||
      key === "medicaid" ||
      key === "medicare"
    ) {
      continue;
    }
    seen.add(key);
    labels.push(label);
  }

  return labels;
}

export function normalizeCasePayerForForm(
  payer: string | null | undefined,
  payerDetail?: string | null
): CasePayerCode {
  const value = payer?.trim();
  if (!value || value === "unknown") {
    return "unknown";
  }
  // Rolling-deploy compatibility for both legacy case representations.
  if (value === "private") {
    return "other";
  }
  if (value === "other" && payerDetail?.trim() === "private_pay") {
    return "private_pay";
  }
  if (CASE_PAYER_VALUES.has(value)) {
    return value as CasePayerCode;
  }
  return "unknown";
}

export function normalizeCasePayerForApi(
  payer: string | null | undefined
): CasePayerCode {
  return normalizeCasePayerForForm(payer);
}

/** Display label for case UI / PDF (“Private insurance”, not “Private”). */
export function formatCasePayerDisplay(
  payer: string | null | undefined,
  payerOther?: string | null,
  payerDetail?: string | null
): string {
  const value = payer?.trim();
  if (!value || value === "unknown") {
    return "—";
  }
  if (value === "private") {
    return "Other — Private insurance";
  }
  if (value === "other" && payerDetail?.trim() === "private_pay") {
    return "Private pay";
  }
  const label = getFundingLabel(value, "case");
  if (value !== "other") {
    return label;
  }

  const detail = normalizeCasePayerDetail(payerDetail);
  if (detail && detail !== CASE_PAYER_OTHER_CUSTOM) {
    return `Other — ${getFundingLabel(detail, "case")}`;
  }

  const freeText = payerOther?.trim();
  if (freeText) {
    return `Other — ${freeText}`;
  }
  if (detail === CASE_PAYER_OTHER_CUSTOM) {
    return "Other — Something else";
  }
  return label;
}

/** Max length for optional Something else free-text (matches DB column). */
export const CASE_PAYER_OTHER_MAX_LENGTH = 200;

export const CASE_PAYER_OTHER_CUSTOM = "other_custom" as const;

/** Known residual codes shown when primary payer is Other (not MN waivers). */
export const CASE_PAYER_OTHER_DETAIL_CODES = [
  "private",
  "veterans_benefits",
  "workers_compensation",
  "ltc_insurance",
  CASE_PAYER_OTHER_CUSTOM,
] as const;

export type CasePayerOtherDetailCode =
  (typeof CASE_PAYER_OTHER_DETAIL_CODES)[number];

export function getCasePayerOtherDetailOptions(): {
  value: CasePayerOtherDetailCode;
  label: string;
}[] {
  return CASE_PAYER_OTHER_DETAIL_CODES.map((code) => ({
    value: code,
    label:
      code === CASE_PAYER_OTHER_CUSTOM
        ? "Something else…"
        : getFundingLabel(code, "case"),
  }));
}

export function normalizeCasePayerDetail(
  detail: string | null | undefined
): CasePayerOtherDetailCode | "" {
  const value = detail?.trim().toLowerCase();
  if (!value) {
    return "";
  }
  return (CASE_PAYER_OTHER_DETAIL_CODES as readonly string[]).includes(value)
    ? (value as CasePayerOtherDetailCode)
    : "";
}

/**
 * Resolve edit-form Other fields from stored payer / detail / free-text
 * (including legacy free-text-only rows).
 */
export function resolveCasePayerOtherFields(input: {
  payer: string | null | undefined;
  payerDetail?: string | null;
  payerOther?: string | null;
}): { payerDetail: string; payerOther: string } {
  if (input.payer?.trim() === "private") {
    return { payerDetail: "private", payerOther: "" };
  }
  if (normalizeCasePayerForForm(input.payer, input.payerDetail) !== "other") {
    return { payerDetail: "", payerOther: "" };
  }
  const explicit = normalizeCasePayerDetail(input.payerDetail);
  if (explicit) {
    return {
      payerDetail: explicit,
      payerOther:
        explicit === CASE_PAYER_OTHER_CUSTOM
          ? input.payerOther?.trim() ?? ""
          : "",
    };
  }
  const legacy = input.payerOther?.trim() ?? "";
  if (!legacy) {
    return { payerDetail: "", payerOther: "" };
  }
  const resolved = resolveFundingCode(legacy);
  if (
    resolved &&
    (CASE_PAYER_OTHER_DETAIL_CODES as readonly string[]).includes(resolved) &&
    resolved !== CASE_PAYER_OTHER_CUSTOM
  ) {
    return { payerDetail: resolved, payerOther: "" };
  }
  return { payerDetail: CASE_PAYER_OTHER_CUSTOM, payerOther: legacy };
}

export const CASE_PAYER_FIELD_HELP_LINES = [
  "Choose the expected primary payment method. Use Other for insurance, benefits, or another funding source.",
] as const;

/** Single-line join for tests / aria consumers that expect one string. */
export const CASE_PAYER_FIELD_HELP = CASE_PAYER_FIELD_HELP_LINES.join(" ");

export const CASE_PAYER_OTHER_PLACEHOLDER =
  "Optional: describe the funding source…";

export const CASE_PAYER_OTHER_DETAIL_HELP =
  "Choose the closest payer or funding source. Use Something else only when none of the listed types apply.";

/**
 * Persistable case funding codes beyond the single primary payer slot.
 * Includes search programs and secondary payer classes so multi-select
 * round-trips (e.g. Medicaid + Medicare) survive storage.
 */
export function normalizeMustHaveFundingCodes(
  raw: string | string[] | null | undefined
): string[] {
  const allowed = new Set(
    FUNDING_TAXONOMY.filter(
      (option) =>
        option.searchFilterReady &&
        option.code !== "any_state_waiver" &&
        (option.searchProgram || option.searchPayerClass)
    ).map((option) => option.code)
  );
  const items = parseFundingRawItems(raw);
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const trimmed = item.trim();
    const code =
      resolveFundingCode(item) ?? (allowed.has(trimmed) ? trimmed : null);
    if (!code || !allowed.has(code) || seen.has(code)) {
      continue;
    }
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

const CASE_FUNDING_PRIMARY_CLASS_PRIORITY = [
  "medicaid",
  "medicare",
  "private_pay",
] as const;

const CASE_FUNDING_RESIDUAL_PRIORITY = [
  "private",
  "veterans_benefits",
  "workers_compensation",
  "ltc_insurance",
] as const;

/**
 * Normalize codes for the unified case Funding multi-select
 * (provider-selectable + search-ready programs/classes; no unknown/other).
 */
export function normalizeCaseFundingSelectionCodes(
  raw: string | string[] | null | undefined
): string[] {
  const allowed = new Set(
    FUNDING_TAXONOMY.filter(
      (option) =>
        option.code !== "any_state_waiver" &&
        option.code !== "unknown" &&
        option.code !== "other" &&
        (option.providerSelectable ||
          (option.searchFilterReady &&
            (option.searchProgram || option.searchPayerClass)))
    ).map((option) => option.code)
  );
  const items = parseFundingRawItems(raw);
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const trimmed = item.trim();
    const code =
      resolveFundingCode(item) ?? (allowed.has(trimmed) ? trimmed : null);
    if (!code || !allowed.has(code) || seen.has(code)) {
      continue;
    }
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

export type CaseFundingStoredFields = {
  payer: string;
  payerDetail: string;
  payerOther: string;
  mustHaveFundingCodes: string[];
};

/**
 * Hydrate unified Funding multi-select codes from stored case payer fields.
 * Unresolvable free-text payerOther is omitted (catalog-only UI).
 */
export function storedCaseFundingToSelection(input: {
  payer?: string | null;
  payerDetail?: string | null;
  payerOther?: string | null;
  mustHaveFundingCodes?: string[] | null;
}): string[] {
  const codes: string[] = [];
  const seen = new Set<string>();
  const push = (code: string | null | undefined) => {
    if (
      !code ||
      seen.has(code) ||
      code === "unknown" ||
      code === "other" ||
      code === CASE_PAYER_OTHER_CUSTOM
    ) {
      return;
    }
    seen.add(code);
    codes.push(code);
  };

  const rawPayer = input.payer?.trim();
  if (rawPayer === "private") {
    push("private");
  } else if (rawPayer === "other" && input.payerDetail?.trim() === "private_pay") {
    push("private_pay");
  } else {
    const formPayer = normalizeCasePayerForForm(rawPayer, input.payerDetail);
    if (
      formPayer === "medicaid" ||
      formPayer === "medicare" ||
      formPayer === "private_pay"
    ) {
      push(formPayer);
    } else if (formPayer === "other") {
      const { payerDetail } = resolveCasePayerOtherFields({
        payer: input.payer,
        payerDetail: input.payerDetail,
        payerOther: input.payerOther,
      });
      if (payerDetail && payerDetail !== CASE_PAYER_OTHER_CUSTOM) {
        push(payerDetail);
      }
    }
  }

  for (const code of normalizeMustHaveFundingCodes(input.mustHaveFundingCodes)) {
    push(code);
  }

  return normalizeCaseFundingSelectionCodes(codes);
}

/**
 * Display labels for case Funding (unified multi-select + legacy free-text Other).
 */
export function formatCaseFundingDisplayLabels(input: {
  payer?: string | null;
  payerDetail?: string | null;
  payerOther?: string | null;
  mustHaveFundingCodes?: string[] | null;
}): string[] {
  const fromSelection = formatFundingAcceptedForDisplay(
    storedCaseFundingToSelection(input),
    "case"
  );
  if (fromSelection.length > 0) {
    return fromSelection;
  }
  const legacy = formatCasePayerDisplay(
    input.payer,
    input.payerOther,
    input.payerDetail
  );
  if (legacy && legacy !== "—") {
    return [legacy];
  }
  return [];
}

/**
 * Dual-write unified Funding multi-select back to payer + mustHaveFundingCodes.
 * Clears free-text payerOther (catalog-only).
 */
export function caseFundingSelectionToStored(
  rawCodes: string | string[] | null | undefined
): CaseFundingStoredFields {
  const codes = normalizeCaseFundingSelectionCodes(rawCodes);
  if (codes.length === 0) {
    return {
      payer: "unknown",
      payerDetail: "",
      payerOther: "",
      mustHaveFundingCodes: [],
    };
  }

  let consumedPrimary: string | null = null;
  let payer = "unknown";
  let payerDetail = "";

  for (const code of CASE_FUNDING_PRIMARY_CLASS_PRIORITY) {
    if (codes.includes(code)) {
      payer = code;
      consumedPrimary = code;
      break;
    }
  }

  if (!consumedPrimary) {
    for (const code of CASE_FUNDING_RESIDUAL_PRIORITY) {
      if (codes.includes(code)) {
        payer = "other";
        payerDetail = code;
        consumedPrimary = code;
        break;
      }
    }
  }

  // If only non-priority programs remain (e.g. CADI alone), keep payer unknown
  // and store everything as must-haves so search still prefills.
  const mustHaveSource = consumedPrimary
    ? codes.filter((code) => code !== consumedPrimary)
    : codes;

  return {
    payer,
    payerDetail,
    payerOther: "",
    mustHaveFundingCodes: normalizeMustHaveFundingCodes(mustHaveSource),
  };
}

/**
 * Case primary payer → search criteria.
 * Known Other residuals map to advanced funding_accepted codes.
 * Optional must-have program codes are merged into fundingAccepted.
 */
type CasePayerSearchCriteria = {
  acceptsMedicaid?: boolean;
  acceptsMedicare?: boolean;
  acceptsPrivateInsurance?: boolean;
  fundingAccepted?: string[];
};

/**
 * Map unified Funding multi-select codes to provider-search prefill criteria.
 */
export function caseFundingCodesToSearchCriteria(
  rawCodes: string | string[] | null | undefined
): CasePayerSearchCriteria {
  const codes = normalizeCaseFundingSelectionCodes(rawCodes);
  if (codes.length === 0) {
    return {};
  }

  const result: CasePayerSearchCriteria = {};
  const fundingAccepted: string[] = [];
  for (const code of codes) {
    if (code === "medicaid") {
      result.acceptsMedicaid = true;
      continue;
    }
    if (code === "medicare") {
      result.acceptsMedicare = true;
      continue;
    }
    fundingAccepted.push(code);
  }
  if (fundingAccepted.length > 0) {
    result.fundingAccepted = fundingAccepted;
  }
  return result;
}

export function casePayerToSearchCriteria(
  payer: string | null | undefined,
  payerDetail?: string | null,
  mustHaveFundingCodes?: string[] | null
): CasePayerSearchCriteria {
  return caseFundingCodesToSearchCriteria(
    storedCaseFundingToSelection({
      payer,
      payerDetail,
      mustHaveFundingCodes,
    })
  );
}

export function fundingCodesToAcceptsFlags(codes: string[]): {
  acceptsMedicaid: boolean;
  acceptsMedicare: boolean;
  acceptsPrivateInsurance: boolean;
  acceptsPrivatePay: boolean;
  acceptsStateWaivers: boolean;
} {
  const flags = {
    acceptsMedicaid: false,
    acceptsMedicare: false,
    acceptsPrivateInsurance: false,
    acceptsPrivatePay: false,
    acceptsStateWaivers: false,
  };

  for (const raw of codes) {
    const code = resolveFundingCode(raw);
    if (!code) {
      continue;
    }
    const option = FUNDING_TAXONOMY.find((row) => row.code === code);
    for (const flag of option?.acceptsFlags ?? []) {
      if (flag === "medicaid") flags.acceptsMedicaid = true;
      if (flag === "medicare") flags.acceptsMedicare = true;
      if (flag === "private_insurance") flags.acceptsPrivateInsurance = true;
      if (flag === "private_pay") flags.acceptsPrivatePay = true;
      if (flag === "state_waivers") flags.acceptsStateWaivers = true;
    }
  }

  return flags;
}

export function resolveFundingCode(
  raw: string | null | undefined
): string | null {
  const value = raw?.trim();
  if (!value) {
    return null;
  }
  return CODE_BY_ALIAS.get(value.toLowerCase()) ?? null;
}

/**
 * Normalize stored funding_accepted (labels or codes) to taxonomy codes.
 * Unmapped strings are dropped (callers may log separately).
 * Search-only codes (any_state_waiver) and unknown are never persisted.
 */
export function normalizeFundingAcceptedStored(
  raw: string | string[] | null | undefined
): string[] {
  return normalizeFundingFilterCodes(raw, { allowSearchOnlyCodes: false });
}

/**
 * Normalize advanced-search funding_accepted filter values to taxonomy codes.
 * Preserves search-only `any_state_waiver` (unlike provider storage normalize).
 */
export function normalizeSearchFundingFilters(
  raw: string | string[] | null | undefined
): string[] {
  return normalizeFundingFilterCodes(raw, { allowSearchOnlyCodes: true });
}

function parseFundingRawItems(
  raw: string | string[] | null | undefined
): string[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item));
      }
      return raw.split(",").map((part) => part.trim());
    } catch {
      return raw.split(",").map((part) => part.trim());
    }
  }
  return [];
}

function normalizeFundingFilterCodes(
  raw: string | string[] | null | undefined,
  options: { allowSearchOnlyCodes: boolean }
): string[] {
  const items = parseFundingRawItems(raw);
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const code = resolveFundingCode(item);
    if (!code || code === "unknown") {
      continue;
    }
    // Provider storage never persists case-payer residual `other` (no free-text peer).
    if (code === "other" && !options.allowSearchOnlyCodes) {
      continue;
    }
    if (code === "any_state_waiver" && !options.allowSearchOnlyCodes) {
      continue;
    }
    if (seen.has(code)) {
      continue;
    }
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

/**
 * Coarse boolean → funding codes only. Never invents mn_* waiver codes
 * from accepts_state_waivers (residual risk §3).
 */
export function acceptsFlagsToDefaultFundingCodes(flags: {
  acceptsMedicaid?: boolean;
  acceptsMedicare?: boolean;
  acceptsPrivateInsurance?: boolean;
  acceptsStateWaivers?: boolean;
}): string[] {
  const codes: string[] = [];
  if (flags.acceptsMedicaid) {
    codes.push("medicaid");
  }
  if (flags.acceptsMedicare) {
    codes.push("medicare");
  }
  if (flags.acceptsPrivateInsurance) {
    // Emit the canonical code, not the legacy "private" import code.
    codes.push("private_pay");
  }
  // intentionally ignore acceptsStateWaivers for default backfill
  void flags.acceptsStateWaivers;
  return codes;
}

/**
 * Convert MultiSelect selections (+ optional free-text Other) to stored taxonomy codes.
 * Selection values are codes; free-text may still be legacy labels resolved via aliases.
 */
export function providerFundingSelectionToCodes(
  selectedValues: string[],
  otherText = ""
): string[] {
  const raw = [
    ...selectedValues.filter((value) => value !== "Other"),
    ...otherText
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean),
  ];
  const codes: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const code = resolveFundingCode(item);
    if (
      !code ||
      code === "any_state_waiver" ||
      code === "unknown" ||
      code === "other"
    ) {
      continue;
    }
    if (seen.has(code)) {
      continue;
    }
    seen.add(code);
    codes.push(code);
  }
  return codes;
}
